import React, { Component } from 'react';
import classNames from 'classnames';

import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { FormattedMessage } from '../../util/reactIntl';
import { ensureCurrentUser } from '../../util/data';
import { isUserAuthorized } from '../../util/userHelpers';
import { pathByRouteName } from '../../util/routes';

import css from './ModalMissingInformation.module.css';

const MISSING_INFORMATION_MODAL_WHITELIST = [
  'LoginPage',
  'SignupPage',
  'ContactDetailsPage',
  'EmailVerificationPage',
  'PasswordResetPage',
  'StripePayoutPage',
  // NOTE: Do NOT add parameterized routes (e.g. InboxPage /inbox/:tab) here —
  // pathByRouteName() throws without params, causing infinite error loop.
  // ManageListingsPage, PaymentMethodsPage intentionally NOT whitelisted —
  // email verification is required before any account actions. (Decision: Aaron, 2026-03-20)
];

const EMAIL_VERIFICATION = 'EMAIL_VERIFICATION';
const SESSION_STORAGE_KEY = 'emailVerificationReminderDismissed';

/**
 * 2026-07-02 (UI/UX audit P0-3): this used to be a scroll-locking Modal whose
 * only dismiss ("Later") rendered below the fold on ~900px-tall screens —
 * effectively a trap that re-armed on navigation and blocked even /s. Same
 * trigger logic, now rendered as a slim dismissible banner under the topbar,
 * with visible feedback when the verification email is resent.
 */
class ModalMissingInformation extends Component {
  constructor(props) {
    super(props);

    // Check if user already dismissed the reminder this session
    let dismissedThisSession = false;
    try {
      dismissedThisSession = typeof window !== 'undefined' &&
        sessionStorage.getItem(SESSION_STORAGE_KEY) === 'true';
    } catch (e) {
      // sessionStorage not available (SSR or private browsing)
    }

    this.state = {
      showMissingInformationReminder: null,
      hasSeenMissingInformationReminder: dismissedThisSession,
      resendClicked: false,
    };
    this.handleMissingInformationReminder = this.handleMissingInformationReminder.bind(this);
    this.handleDismiss = this.handleDismiss.bind(this);
    this.handleResend = this.handleResend.bind(this);
  }

  componentDidUpdate() {
    const { currentUser, currentUserHasListings, currentUserHasOrders, location } = this.props;
    const user = ensureCurrentUser(currentUser);
    this.handleMissingInformationReminder(
      user,
      currentUserHasListings,
      currentUserHasOrders,
      location
    );
  }

  handleMissingInformationReminder(
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    newLocation
  ) {
    const routes = this.props.routeConfiguration;
    const whitelistedPaths = MISSING_INFORMATION_MODAL_WHITELIST.map(page =>
      pathByRouteName(page, routes)
    );

    // Is the current page whitelisted?
    const isPageWhitelisted = whitelistedPaths.includes(newLocation.pathname);

    // Only show once per session — if user dismissed, don't nag again on navigation
    if (this.state.hasSeenMissingInformationReminder) {
      return;
    }

    // Track if path changes inside Page level component
    const pathChanged = newLocation.pathname !== this.props.location.pathname;
    const notRemindedYet =
      !this.state.showMissingInformationReminder && !this.state.hasSeenMissingInformationReminder;

    // Is the reminder already shown on current page
    const showOnPathChange = notRemindedYet || pathChanged;

    if (!isPageWhitelisted && showOnPathChange) {
      // Emails are sent when order is initiated
      // Customer is likely to get email soon when she books something
      // Provider email should work - she should get an email when someone books a listing
      const hasOrders = currentUserHasOrders === true;
      const hasListingsOrOrders = currentUserHasListings || hasOrders;

      const emailUnverified = !!currentUser.id && !currentUser.attributes.emailVerified;
      const emailVerificationNeeded = hasListingsOrOrders && emailUnverified;

      // Show reminder
      if (emailVerificationNeeded) {
        this.setState({ showMissingInformationReminder: EMAIL_VERIFICATION });
      }
    }
  }

  handleDismiss() {
    // Persist dismissal to sessionStorage so the banner doesn't reappear on navigation
    try {
      if (typeof window !== 'undefined') {
        sessionStorage.setItem(SESSION_STORAGE_KEY, 'true');
      }
    } catch (e) {
      // sessionStorage not available
    }
    this.setState({
      showMissingInformationReminder: null,
      hasSeenMissingInformationReminder: true,
    });
  }

  handleResend() {
    this.setState({ resendClicked: true });
    this.props.onResendVerificationEmail();
  }

  render() {
    const {
      rootClassName,
      className,
      currentUser,
      sendVerificationEmailInProgress,
      sendVerificationEmailError,
    } = this.props;

    const user = ensureCurrentUser(currentUser);
    const currentUserLoaded = user && user.id;

    const showBanner =
      this.state.showMissingInformationReminder === EMAIL_VERIFICATION &&
      currentUserLoaded &&
      isUserAuthorized(currentUser);

    if (!showBanner) {
      return null;
    }

    const email = user.attributes.email;
    const resendSucceeded =
      this.state.resendClicked && !sendVerificationEmailInProgress && !sendVerificationEmailError;

    const resendLabel = sendVerificationEmailInProgress ? (
      <FormattedMessage id="ModalMissingInformation.bannerSending" />
    ) : resendSucceeded ? (
      <FormattedMessage id="ModalMissingInformation.bannerSent" />
    ) : (
      <FormattedMessage id="ModalMissingInformation.bannerResend" />
    );

    const classes = classNames(rootClassName || css.root, className);

    return (
      <div className={classes} role="status">
        <span className={css.bannerText}>
          <FormattedMessage
            id="ModalMissingInformation.bannerText"
            values={{ email: <span className={css.bannerEmail}>{email}</span> }}
          />
          {sendVerificationEmailError ? (
            <span className={css.bannerError}>
              <FormattedMessage id="ModalMissingInformation.resendFailed" />
            </span>
          ) : null}
        </span>
        <button
          type="button"
          className={css.bannerResend}
          onClick={this.handleResend}
          disabled={sendVerificationEmailInProgress || resendSucceeded}
        >
          {resendLabel}
        </button>
        <button
          type="button"
          className={css.bannerDismiss}
          onClick={this.handleDismiss}
          aria-label="Dismiss verification reminder"
        >
          ✕
        </button>
      </div>
    );
  }
}

/**
 * Slim banner that reminds a user with listings or orders to verify their
 * email address. Dismissible (persists for the session), with resend
 * feedback. Replaces the old blocking modal (UI/UX audit P0-3).
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.containerClassName kept for API compatibility (unused since the modal became a banner)
 * @param {string} props.id
 * @param {Object} props.currentUser API entity
 * @param {Function} props.onManageDisableScrolling kept for API compatibility (banner never locks scroll)
 * @param {Object?} props.sendVerificationEmailError
 * @param {boolean} props.sendVerificationEmailInProgress
 * @returns {JSX.Element|null} banner element if the user needs the reminder
 */
const EnhancedModalMissingInformation = props => {
  const routeConfiguration = useRouteConfiguration();

  return <ModalMissingInformation routeConfiguration={routeConfiguration} {...props} />;
};

export default EnhancedModalMissingInformation;
